import { createClient } from "@supabase/supabase-js";

// Supabase-Konfiguration
const SUPABASE_URL = "https://iabdippnknqmozheiulb.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlhYmRpcHBua25xbW96aGVpdWxiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzIwNDExNzQsImV4cCI6MjA0NzYxNzE3NH0.KfZR5YVwVQcrbToe9NzdUvJD8jbkeKOq8DRlM87F0Js";

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// Date → 'YYYY-MM-DD' in LOKALER Zeitzone (toISOString würde nach UTC verschieben
// und in CET/CEST das Datum des Vortags liefern).
export function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Registriert einen neuen Benutzer.
 */
export async function registerUser(email, password, username, role = "customer") {
  try {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    if (!data.user) {
      throw new Error("Benutzer konnte nicht erstellt werden.");
    }

    console.log("Registrierte Benutzer-ID:", data.user.id);
    console.log("Registrierte E-Mail:", email.toLowerCase().trim());

    const { error: userError } = await supabase.from("users").insert({
      id: data.user.id,
      email: email.toLowerCase().trim(),
      username,
      role,
    });

    if (userError) throw userError;

    console.log("Benutzer erfolgreich in der Tabelle users gespeichert.");
    return null; // Erfolgreich
  } catch (error) {
    console.error("Fehler bei der Registrierung:", error.message);
    return `Registrierung fehlgeschlagen: ${error.message}`;
  }
}

/**
 * Loggt einen Benutzer ein.
 */
export async function loginUser(identifier, password) {
  try {
    console.log("Login versucht mit:", identifier, password);

    // Authentifizieren
    const { data, error } = await supabase.auth.signInWithPassword({
      email: identifier.toLowerCase().trim(),
      password,
    });

    if (error) {
      console.error("Fehler bei der Authentifizierung:", error.message);
      return "Invalid login credentials";
    }

    console.log("Login erfolgreich:", data);

    // Benutzerinformationen abrufen
    const { data: userInfo, error: userError } = await supabase
      .from("users")
      .select("username, role")
      .eq("email", identifier.toLowerCase().trim())
      .maybeSingle();

    console.log("Benutzerabfrage-Ergebnis:", userInfo);

    if (userError) {
      console.error("Fehler bei der Benutzerabfrage:", userError.message);
      return "Fehler beim Abrufen der Benutzerinformationen.";
    }

    if (!userInfo) {
      console.error("Keine Benutzerinformationen gefunden.");
      return "Benutzer existiert nicht oder ist nicht registriert.";
    }

    return { user: data.user, role: userInfo.role };
  } catch (error) {
    console.error("Fehler beim Login:", error.message);
    return "Login fehlgeschlagen.";
  }
}

/**
 * Zurücksetzen des Passworts.
 */
export async function resetPassword(email) {
  try {
    // Prüfen ob User existiert
    const { data: userExists, error: userError } = await supabase
      .from("users")
      .select("email")
      .eq("email", email.toLowerCase().trim())
      .maybeSingle();

    if (userError) {
      console.error("Fehler beim Prüfen des Users:", userError.message);
      return "Fehler beim Prüfen der E-Mail-Adresse.";
    }

    if (!userExists) {
      return "Diese E-Mail-Adresse ist nicht registriert.";
    }

    // Echte Supabase Auth - sendet E-Mail mit dem schönen Template
    console.log("📧 Sende Password Reset E-Mail an:", email);
    
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `https://saficuts.de/update-password`
    });
    
    if (error) {
      console.error("❌ Supabase Auth Error:", error);
      throw error;
    }
    
    console.log("✅ Password reset E-Mail erfolgreich gesendet!");
    console.log("📬 Prüfe dein E-Mail-Postfach (auch Spam-Ordner)");
    return null; // Erfolgreich
  } catch (error) {
    console.error("Fehler beim Zurücksetzen des Passworts:", error.message);
    return "Fehler beim Zurücksetzen des Passworts.";
  }
}

/**
 * Holt alle Termine aus der `appointments`-Tabelle.
 */
export async function fetchAppointments() {
  try {
    const { data, error } = await supabase.from("appointments").select("*");
    if (error) throw error;
    console.log("Termine erfolgreich abgerufen:", data);
    return data;
  } catch (error) {
    console.error("Fehler beim Abrufen der Termine:", error.message);
    return [];
  }
}

/**
 * Fügt einen neuen Termin hinzu.
 */
export async function addAppointment(appointment) {
  try {
    if (!appointment.date || !appointment.time) {
      return { data: null, error: "Datum und Zeit müssen angegeben werden." };
    }

    const datetime = new Date(appointment.date);
    if (isNaN(datetime.getTime())) {
      return { data: null, error: "Ungültiges Datumsformat." };
    }

    // Prüfen auf Doppelbuchung (Datum und Zeit)
    const { data: conflictingAppointments, error: conflictError } = await supabase
      .from("appointments")
      .select("*")
      .eq("date", appointment.date)
      .eq("time", appointment.time);

    if (conflictError) throw conflictError;

    if (conflictingAppointments.length > 0) {
      return {
        data: null,
        error: "Doppelbuchung erkannt. Diese Zeit ist bereits vergeben.",
      };
    }

    // Termin hinzufügen
    const { data, error } = await supabase
      .from("appointments")
      .insert({
        ...appointment,
      })
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (err) {
    console.error("Fehler beim Hinzufügen des Termins:", err.message);
    return { data: null, error: err.message };
  }
}

/**
 * Aktualisiert einen Termin.
 */
export async function updateAppointment(id, updates) {
  try {
    if (!updates.date || !updates.time) {
      return { data: null, error: "Datum und Zeit müssen angegeben werden." };
    }

    const datetime = new Date(updates.date);
    if (isNaN(datetime.getTime())) {
      return { data: null, error: "Ungültiges Datumsformat." };
    }

    // Überprüfung auf Doppelbuchung (Datum und Zeit)
    const { data: existingAppointments, error: checkError } = await supabase
      .from("appointments")
      .select("*")
      .eq("date", updates.date)
      .eq("time", updates.time)
      .neq("id", id); // Ausschließen des aktuellen Termins

    if (checkError) throw checkError;

    if (existingAppointments.length > 0) {
      return {
        data: null,
        error: "Diese Zeit ist bereits gebucht. Bitte wählen Sie eine andere Zeit.",
      };
    }

    // Termin aktualisieren
    const { data, error } = await supabase
      .from("appointments")
      .update(updates)
      .eq("id", id)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Termins:", error.message);
    return { data: null, error: error.message };
  }
}

/**
 * Löscht einen Termin.
 */
export async function deleteAppointment(id) {
  try {
    console.log("Lösche Termin mit ID:", id);

    const { data, error } = await supabase.from("appointments").delete().eq("id", id).select();
    if (error) {
      console.error("Fehler beim Löschen des Termins:", error.message);
      return { data: null, error };
    }

    if (!data || data.length === 0) {
      console.error("Kein Termin gelöscht. Prüfe die ID:", id);
      return { data: null, error: new Error("Kein Termin gelöscht.") };
    }

    console.log("Termin erfolgreich gelöscht:", data);
    return { data, error: null };
  } catch (error) {
    console.error("Fehler beim Löschen des Termins (Catch):", error.message);
    return { data: null, error };
  }
}

/**
 * Holt alle verfügbaren Zeiten für ein bestimmtes Datum aus der `appointments`-Tabelle.
 */
export async function fetchAvailableTimes(date) {
  try {
    const { data: bookedAppointments, error } = await supabase
      .from("appointments")
      .select("time")
      .eq("date", date);

    if (error) throw error;

    const bookedTimes = bookedAppointments.map((appointment) => appointment.time);

    // Liste aller möglichen Zeiten
    const allTimes = ["19:00", "19:30", "20:00", "20:30", "21:00", "21:30"];
    
    // Verfügbare Zeiten berechnen
    const availableTimes = allTimes.filter((time) => !bookedTimes.includes(time));
    return { availableTimes, error: null };
  } catch (error) {
    console.error("Fehler beim Abrufen der verfügbaren Zeiten:", error.message);
    return { availableTimes: [], error: error.message };
  }
}

/**
 * Schichtsystem - Schichttypen:
 * - "fruehschicht": Termine nur täglich von 16:00-20:00 Uhr (anpassbar)
 * - "spaetschicht": Unter der Woche keine Termine, nur Sa+So von 10:00-17:00 Uhr (anpassbar)
 * - "nachtschicht": Alle Tage blockiert (keine Termine möglich)
 * - "normal": Standardbetrieb (Mo-Fr, 09:00-19:30) (anpassbar)
 */

// Default Schichteinstellungen (Fallback) - Pro Tag individuelle Zeiten
const DEFAULT_SHIFT_SETTINGS = {
  normal: { 
    label: "Normalbetrieb",
    schedule: {
      0: null, // Sonntag - deaktiviert
      1: { start: "09:00", end: "19:30" }, // Montag
      2: { start: "09:00", end: "19:30" }, // Dienstag
      3: { start: "09:00", end: "19:30" }, // Mittwoch
      4: { start: "09:00", end: "19:30" }, // Donnerstag
      5: { start: "09:00", end: "19:30" }, // Freitag
      6: null  // Samstag - deaktiviert
    }
  },
  fruehschicht: { 
    label: "Frühschicht",
    schedule: {
      0: { start: "16:00", end: "20:00" }, // Sonntag
      1: { start: "16:00", end: "20:00" }, // Montag
      2: { start: "16:00", end: "20:00" }, // Dienstag
      3: { start: "16:00", end: "20:00" }, // Mittwoch
      4: { start: "16:00", end: "20:00" }, // Donnerstag
      5: { start: "16:00", end: "20:00" }, // Freitag
      6: { start: "16:00", end: "20:00" }  // Samstag
    }
  },
  spaetschicht: { 
    label: "Spätschicht",
    schedule: {
      0: { start: "10:00", end: "17:00" }, // Sonntag
      1: null, // Montag - deaktiviert
      2: null, // Dienstag - deaktiviert
      3: null, // Mittwoch - deaktiviert
      4: null, // Donnerstag - deaktiviert
      5: null, // Freitag - deaktiviert
      6: { start: "10:00", end: "17:00" }  // Samstag
    }
  },
  nachtschicht: { 
    label: "Nachtschicht",
    schedule: {
      0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null
    }
  }
};

/**
 * Holt die aktuelle Schichteinstellung aus der Datenbank.
 */
export async function getShiftSetting() {
  try {
    const { data, error } = await supabase
      .from("admin_settings")
      .select("value")
      .eq("key", "current_shift")
      .single();

    if (error && error.code !== "PGRST116") throw error;

    return { shift: data?.value || "normal", error: null };
  } catch (error) {
    console.error("Fehler beim Abrufen der Schichteinstellung:", error.message);
    return { shift: "normal", error: error.message };
  }
}

/**
 * Speichert die Schichteinstellung in der Datenbank.
 * Wenn startDate angegeben, wird es als geplante Schicht gespeichert.
 */
export async function setShiftSetting(shift, startDate = null) {
  try {
    const today = toLocalDateStr(new Date());
    
    // Wenn Startdatum heute oder früher ist, setze als aktuelle Schicht
    if (!startDate || startDate <= today) {
      const { data: existing, error: fetchError } = await supabase
        .from("admin_settings")
        .select("id")
        .eq("key", "current_shift")
        .single();

      if (fetchError && fetchError.code !== "PGRST116") throw fetchError;

      if (existing) {
        const { error } = await supabase
          .from("admin_settings")
          .update({ value: shift, updated_at: new Date().toISOString() })
          .eq("key", "current_shift");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("admin_settings")
          .insert({ key: "current_shift", value: shift });
        if (error) throw error;
      }
      
      // Deaktiviere geplante Schicht wenn vorhanden
      await supabase.from("admin_settings").update({ value: '', updated_at: new Date().toISOString() }).eq("key", "upcoming_shift");
    } else {
      // Speichere als geplante Schicht
      const upcomingValue = JSON.stringify({ shift, startDate });
      
      const { data: existing, error: fetchError } = await supabase
        .from("admin_settings")
        .select("id")
        .eq("key", "upcoming_shift")
        .single();

      if (fetchError && fetchError.code !== "PGRST116") throw fetchError;

      if (existing) {
        const { error } = await supabase
          .from("admin_settings")
          .update({ value: upcomingValue, updated_at: new Date().toISOString() })
          .eq("key", "upcoming_shift");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("admin_settings")
          .insert({ key: "upcoming_shift", value: upcomingValue });
        if (error) throw error;
      }
    }

    return { error: null };
  } catch (error) {
    console.error("Fehler beim Speichern der Schichteinstellung:", error.message);
    return { error: error.message };
  }
}

/**
 * Holt den Schichtplan (Array von geplanten Schichten) aus der Datenbank.
 * Format: [{ shift: "fruehschicht", startDate: "2026-01-10", endDate: "2026-01-16" }, ...]
 */
export async function getPlannedShifts() {
  try {
    const { data, error } = await supabase
      .from("admin_settings")
      .select("value")
      .eq("key", "planned_shifts")
      .single();

    if (error && error.code !== "PGRST116") throw error;

    if (!data?.value || data.value === '' || data.value === 'null') {
      return { plannedShifts: [], error: null };
    }

    try {
      const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
      if (Array.isArray(parsed)) {
        // Sortiere nach startDate
        const sorted = parsed.sort((a, b) => a.startDate.localeCompare(b.startDate));
        return { plannedShifts: sorted, error: null };
      }
      return { plannedShifts: [], error: null };
    } catch (e) {
      return { plannedShifts: [], error: null };
    }
  } catch (error) {
    console.error("Fehler beim Abrufen des Schichtplans:", error.message);
    return { plannedShifts: [], error: error.message };
  }
}

/**
 * Fügt eine neue geplante Schicht zum Schichtplan hinzu.
 * Wenn eine bestehende Schicht kein Enddatum hat und die neue Schicht danach beginnt,
 * wird die bestehende Schicht automatisch beendet (Enddatum = newStartDate - 1 Tag).
 */
export async function addPlannedShift(shift, startDate, endDate = null) {
  try {
    let { plannedShifts } = await getPlannedShifts();
    
    // Neue Schicht hinzufügen
    const newShift = { shift, startDate };
    if (endDate) newShift.endDate = endDate;
    
    // Prüfe auf echte Überlappungen (nur wenn beide Enddaten haben)
    const hasOverlap = plannedShifts.some(ps => {
      // Wenn bestehende Schicht kein Enddatum hat, ist es keine Überlappung
      // (sie wird automatisch beendet)
      if (!ps.endDate) return false;
      
      const psEnd = ps.endDate;
      const newEnd = endDate || '9999-12-31';
      
      // Echte Überlappung: Zeiträume überschneiden sich
      return (startDate <= psEnd && newEnd >= ps.startDate);
    });
    
    if (hasOverlap) {
      return { error: "Überlappung mit bestehender Schichtplanung!" };
    }
    
    // Wenn eine bestehende Schicht ohne Enddatum VOR der neuen beginnt,
    // setze ihr Enddatum auf (newStartDate - 1 Tag)
    plannedShifts = plannedShifts.map(ps => {
      if (!ps.endDate && ps.startDate < startDate) {
        // String → lokales Date (Mittag um DST-Sprünge zu vermeiden) → -1 Tag → lokaler YYYY-MM-DD
        const [y, m, d] = startDate.split('-').map(Number);
        const dayBefore = new Date(y, m - 1, d, 12);
        dayBefore.setDate(dayBefore.getDate() - 1);
        return { ...ps, endDate: toLocalDateStr(dayBefore) };
      }
      return ps;
    });
    
    plannedShifts.push(newShift);
    
    // Sortiere und speichere
    const sorted = plannedShifts.sort((a, b) => a.startDate.localeCompare(b.startDate));
    
    const { error } = await supabase
      .from("admin_settings")
      .upsert({ 
        key: "planned_shifts", 
        value: JSON.stringify(sorted), 
        updated_at: new Date().toISOString() 
      }, { 
        onConflict: 'key' 
      });
    
    if (error) throw error;
    return { plannedShifts: sorted, error: null };
  } catch (error) {
    console.error("Fehler beim Hinzufügen der Schicht:", error.message);
    return { error: error.message };
  }
}

/**
 * Entfernt eine geplante Schicht aus dem Schichtplan.
 */
export async function removePlannedShift(index) {
  try {
    const { plannedShifts } = await getPlannedShifts();
    
    if (index < 0 || index >= plannedShifts.length) {
      return { error: "Ungültiger Index" };
    }
    
    plannedShifts.splice(index, 1);
    
    const { error } = await supabase
      .from("admin_settings")
      .upsert({ 
        key: "planned_shifts", 
        value: JSON.stringify(plannedShifts), 
        updated_at: new Date().toISOString() 
      }, { 
        onConflict: 'key' 
      });
    
    if (error) throw error;
    return { plannedShifts, error: null };
  } catch (error) {
    console.error("Fehler beim Entfernen der Schicht:", error.message);
    return { error: error.message };
  }
}

/**
 * Löscht den gesamten Schichtplan.
 */
export async function clearPlannedShifts() {
  try {
    const { error } = await supabase
      .from("admin_settings")
      .upsert({ 
        key: "planned_shifts", 
        value: '[]', 
        updated_at: new Date().toISOString() 
      }, { 
        onConflict: 'key' 
      });
    
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error("Fehler beim Löschen des Schichtplans:", error.message);
    return { error: error.message };
  }
}

// Legacy-Kompatibilität für getUpcomingShift
export async function getUpcomingShift() {
  const { plannedShifts } = await getPlannedShifts();
  const today = toLocalDateStr(new Date());
  
  // Finde die nächste geplante Schicht ab heute
  const upcoming = plannedShifts.find(ps => ps.startDate > today);
  return { upcoming: upcoming || null, error: null };
}

// Legacy-Kompatibilität für clearUpcomingShift
export async function clearUpcomingShift() {
  return await clearPlannedShifts();
}

/**
 * Ermittelt welche Schicht für ein bestimmtes Datum gilt.
 * Prüft zuerst den Schichtplan, dann die aktuelle Schicht.
 */
export function getShiftForDate(date, currentShift, plannedShiftsOrUpcoming) {
  const dateStr = date instanceof Date
    ? toLocalDateStr(date)
    : date;
  
  // Wenn ein Array (neues Format) übergeben wurde
  if (Array.isArray(plannedShiftsOrUpcoming)) {
    for (const ps of plannedShiftsOrUpcoming) {
      const endDate = ps.endDate || '9999-12-31';
      if (dateStr >= ps.startDate && dateStr <= endDate) {
        return ps.shift;
      }
    }
    return currentShift;
  }
  
  // Legacy: Einzelnes upcoming shift Objekt
  if (plannedShiftsOrUpcoming && plannedShiftsOrUpcoming.startDate) {
    if (dateStr >= plannedShiftsOrUpcoming.startDate) {
      return plannedShiftsOrUpcoming.shift;
    }
  }
  
  return currentShift;
}

/**
 * Holt alle Schichteinstellungen (benutzerdefinierte Zeiten und Tage).
 */
export async function getShiftSettings() {
  try {
    const { data, error } = await supabase
      .from("admin_settings")
      .select("value")
      .eq("key", "shift_settings")
      .single();

    if (error && error.code !== "PGRST116") throw error;

    if (data?.value) {
      try {
        const settings = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        return { settings: { ...DEFAULT_SHIFT_SETTINGS, ...settings }, error: null };
      } catch (parseError) {
        console.error("Fehler beim Parsen der Schichteinstellungen:", parseError);
        return { settings: DEFAULT_SHIFT_SETTINGS, error: null };
      }
    }

    return { settings: DEFAULT_SHIFT_SETTINGS, error: null };
  } catch (error) {
    console.error("Fehler beim Abrufen der Schichteinstellungen:", error.message);
    return { settings: DEFAULT_SHIFT_SETTINGS, error: error.message };
  }
}

/**
 * Speichert die Einstellungen für eine einzelne Schicht.
 */
export async function updateShiftSettings(shiftKey, shiftConfig) {
  try {
    // Hole aktuelle Einstellungen
    const { settings: currentSettings } = await getShiftSettings();
    
    // Update die spezifische Schicht
    const newSettings = {
      ...currentSettings,
      [shiftKey]: { ...currentSettings[shiftKey], ...shiftConfig }
    };

    // Versuche zuerst zu updaten
    const { data: existing, error: fetchError } = await supabase
      .from("admin_settings")
      .select("id")
      .eq("key", "shift_settings")
      .single();

    if (fetchError && fetchError.code !== "PGRST116") throw fetchError;

    const settingsJson = JSON.stringify(newSettings);

    if (existing) {
      const { error } = await supabase
        .from("admin_settings")
        .update({ value: settingsJson, updated_at: new Date().toISOString() })
        .eq("key", "shift_settings");

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("admin_settings")
        .insert({ key: "shift_settings", value: settingsJson });

      if (error) throw error;
    }

    return { error: null };
  } catch (error) {
    console.error("Fehler beim Speichern der Schichteinstellungen:", error.message);
    return { error: error.message };
  }
}

/**
 * Generiert Zeitslots zwischen Start- und Endzeit im 30-Min-Takt.
 */
function generateTimeSlotsFromRange(startTime, endTime) {
  if (!startTime || !endTime) return [];
  
  const slots = [];
  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);
  
  let hour = startH;
  let minute = startM;
  
  while (hour < endH || (hour === endH && minute <= endM)) {
    const formatted = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
    slots.push(formatted);
    minute += 30;
    if (minute >= 60) {
      hour += 1;
      minute -= 60;
    }
  }
  
  return slots;
}

/**
 * Hilfsfunktion: Gibt verfügbare Zeitslots für einen bestimmten Tag basierend auf Schicht zurück.
 */
export function getTimeSlotsForShift(shift, shiftSettings = null, date = null) {
  const settings = shiftSettings || DEFAULT_SHIFT_SETTINGS;
  const config = settings[shift];
  
  if (!config || !config.schedule) {
    return generateTimeSlotsFromRange("09:00", "19:30");
  }

  // Wenn ein Datum übergeben wurde, hole die Zeiten für diesen spezifischen Tag
  if (date) {
    const day = date.getDay();
    const daySchedule = config.schedule[day];
    if (!daySchedule) return [];
    return generateTimeSlotsFromRange(daySchedule.start, daySchedule.end);
  }

  // Ohne Datum: Sammle alle möglichen Slots aus allen aktiven Tagen
  const allSlots = new Set();
  Object.values(config.schedule).forEach(daySchedule => {
    if (daySchedule) {
      generateTimeSlotsFromRange(daySchedule.start, daySchedule.end).forEach(slot => allSlots.add(slot));
    }
  });
  
  return Array.from(allSlots).sort();
}

/**
 * Hilfsfunktion: Holt die Zeiten für einen bestimmten Tag und Schicht.
 */
export function getDaySchedule(shift, day, shiftSettings = null) {
  const settings = shiftSettings || DEFAULT_SHIFT_SETTINGS;
  const config = settings[shift];
  
  if (!config || !config.schedule) return null;
  return config.schedule[day] || null;
}

/**
 * Hilfsfunktion: Prüft ob ein Tag für die Schicht verfügbar ist.
 */
export function isDayAvailableForShift(date, shift, shiftSettings = null) {
  const day = date.getDay(); // 0 = Sonntag, 6 = Samstag
  const settings = shiftSettings || DEFAULT_SHIFT_SETTINGS;
  const config = settings[shift];

  if (!config || !config.schedule) return false;
  
  // Tag ist verfügbar wenn er einen Schedule hat (nicht null)
  return config.schedule[day] !== null && config.schedule[day] !== undefined;
}

/**
 * Exportiere Default-Einstellungen für UI
 */
export { DEFAULT_SHIFT_SETTINGS };
